import { useEffect, useState } from "react";
import * as api from "../api/client";

const MODEL_KEY = "fv-model";
const API_KEY = "fv-api-key";

/** What the next turn should be answered on. Read at send time rather than passed down as props:
 * the picker lives in the top nav and the chat lives under a route, so there is no component tree
 * between them to thread state through, and a turn only ever needs the value as it stands when the
 * user hits Send. */
export function currentModelSelection() {
  const model = localStorage.getItem(MODEL_KEY) || "";
  return { model, apiKey: model.includes(":") ? "" : sessionStorage.getItem(API_KEY) || "" };
}

/** Model selector for the top nav.
 *
 * The self-hosted model is reachable without a credential, so no key field is shown for it. Picking a
 * hosted model reveals the key input — unless the server already has one in .env (`keyConfigured`),
 * in which case prompting for it would be noise.
 *
 * The key is held in sessionStorage, not localStorage: it clears when the browser closes, so a shared
 * or unattended machine doesn't keep a working credential on disk indefinitely. For a key you don't
 * want to re-enter, put NVIDIA_API_KEY in the server's .env instead — then it never reaches the
 * browser at all.
 */
export default function ModelPicker() {
  const [models, setModels] = useState([]);
  const [model, setModel] = useState(() => localStorage.getItem(MODEL_KEY) || "");
  const [apiKey, setApiKey] = useState(() => sessionStorage.getItem(API_KEY) || "");
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      api
        .listModels()
        .then((d) => {
          if (cancelled) return;
          setModels(d.models || []);
          const saved = localStorage.getItem(MODEL_KEY);
          if (!(d.models || []).some((m) => m.id === saved)) {
            setModel(d.default);
            localStorage.setItem(MODEL_KEY, d.default);
          }
        })
        .catch(() => {});
    load();
    window.addEventListener("llm-models-changed", load);
    return () => {
      cancelled = true;
      window.removeEventListener("llm-models-changed", load);
    };
  }, []);

  const selected = models.find((m) => m.id === model);
  const needsKey = selected?.requiresApiKey && !selected?.keyConfigured;

  const pick = (id) => {
    setModel(id);
    localStorage.setItem(MODEL_KEY, id);
  };

  const setKey = (k) => {
    setApiKey(k);
    sessionStorage.setItem(API_KEY, k);
  };

  if (!models.length) return null;
  const groups = [];
  for (const m of models) {
    const vendor = m.provider === "prod-vl" ? "Self-hosted" : m.vendor;
    const g = groups.find(([v]) => v === vendor);
    if (g) g[1].push(m);
    else groups.push([vendor, [m]]);
  }

  return (
    <div className="model-picker">
      <select
        className="model-select"
        value={model}
        onChange={(e) => pick(e.target.value)}
        title={selected?.note || selected?.label}
        aria-label="Model"
      >
        {groups.map(([vendor, items]) => (
          <optgroup key={vendor} label={vendor}>
            {items.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
                {m.requiresApiKey && !m.keyConfigured ? " · key" : ""}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {selected?.external && (
        <span className="model-ext" title={selected.note || `Questions and the data needed to answer them are sent to ${selected.vendor}.`}>
          External
        </span>
      )}

      {needsKey && (
        <span className="model-key-wrap">
          <input
            className={"model-key" + (apiKey ? "" : " missing")}
            type={showKey ? "text" : "password"}
            value={apiKey}
            placeholder="nvapi-… required"
            onChange={(e) => setKey(e.target.value)}
            aria-label="NVIDIA API key"
            autoComplete="off"
            spellCheck="false"
          />
          <button type="button" className="model-eye" onClick={() => setShowKey((s) => !s)}
                  title={showKey ? "Hide key" : "Show key"}>
            {showKey ? "◎" : "◉"}
          </button>
        </span>
      )}
    </div>
  );
}
